/**
 * SideFlyout -- Right-side overlay flyout panel.
 *
 * Renders as a portal on document.body with:
 * - Backdrop overlay with click-to-close
 * - Right-aligned panel (~50% viewport on desktop, full width on mobile)
 * - Focus trap, ESC-to-close, close button in header
 * - Respects prefers-reduced-motion for slide animation
 *
 * Usage:
 *   <SideFlyout open={isOpen} onOpenChange={setIsOpen} title="Settings">
 *     <SettingsForm />
 *   </SideFlyout>
 */

import type { KeyboardEvent, ReactNode } from "react";
import { useCallback, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/cn";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import { Icon } from "./Icon";

// ---------------------------------------------------------------------------
// Focus trap
// ---------------------------------------------------------------------------

const FOCUSABLE_SELECTOR =
  "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

const trapFocus = (container: HTMLElement, event: KeyboardEvent<HTMLElement>) => {
  const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  if (focusable.length === 0) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;

  if (event.shiftKey && active === first) {
    event.preventDefault();
    last?.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first?.focus();
  }
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SideFlyoutProps = {
  /** Whether the flyout is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Title displayed in the flyout header */
  title: string;
  /** Optional icon name (Iconify format) for the header */
  icon?: string;
  /** Flyout content */
  children: ReactNode;
  /** Optional aria-labelledby override (auto-generated if omitted) */
  titleId?: string;
  /** Optional explicit panel id (for aria-controls on trigger buttons) */
  panelId?: string;
  /** Optional className for the panel element */
  className?: string;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SideFlyout = ({
  open,
  onOpenChange,
  title,
  icon,
  children,
  titleId: titleIdProp,
  panelId: panelIdProp,
  className,
}: SideFlyoutProps) => {
  const autoTitleId = useId();
  const autoPanelId = useId();
  const titleId = titleIdProp ?? autoTitleId;
  const panelId = panelIdProp ?? autoPanelId;
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  const close = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  // -- Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // -- Save and restore focus
  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [open]);

  // -- Auto-focus the close button when opened
  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 10);
    return () => window.clearTimeout(timer);
  }, [open]);

  // -- ESC key handler
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        close();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, close]);

  // -- Focus trap on Tab key
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Tab" && panelRef.current) {
      trapFocus(panelRef.current, event);
    }
  };

  if (!open) return null;

  const content = (
    <div className="fixed inset-0 z-50" role="presentation">
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
        aria-label={`Close ${title}`}
        onClick={close}
        tabIndex={-1}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        id={panelId}
        onKeyDown={handleKeyDown}
        className={cn(
          "absolute right-0 top-0 h-full flex flex-col",
          "w-full sm:w-[min(100vw,50%)]",
          "border-l border-border/60 bg-card shadow-2xl",
          !prefersReducedMotion && "animate-in slide-in-from-right duration-200",
          className,
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border/50 bg-muted/20 px-5 py-3 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {icon && (
              <Icon icon={icon} className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
            )}
            <h2 id={titleId} className="text-sm font-medium text-foreground truncate">
              {title}
            </h2>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={close}
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-md shrink-0",
              "text-muted-foreground hover:text-foreground hover:bg-muted/60",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70",
              "transition-colors"
            )}
            aria-label={`Close ${title}`}
          >
            <Icon icon="lucide:x" className="h-4 w-4" aria-hidden />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto qb-scroll">
          {children}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};
